import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, IconButton, Tooltip } from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MenuIcon from "@mui/icons-material/Menu";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AutoDeleteOutlinedIcon from "@mui/icons-material/AutoDeleteOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import { get, ref } from "firebase/database";

import { AuthContext } from "../../context/AuthContext";
import { database } from "../../firebase";
import SchedulerComponent from "../SchedulerComponent";
import AutoDeleteCommponent from "../AutoDeleteCommponent";
import "./sidebar.scss";

const COLLAPSE_KEY = "wg:sidebarCollapsed";
export const SIDEBAR_TOGGLE_EVENT = "wg:toggle-sidebar";

const SideBarForRole7 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, dispatch: authDispatch } = useContext(AuthContext);

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openScheduler, setOpenScheduler] = useState(false);
  const [openAutoDeleteSettings, setOpenAutoDeleteSettings] = useState(false);

  const pathParts = location.pathname.split("/");
  const watchId = pathParts[4];

  // Publish the rail width to the document so every page's <main> reacts
  // without the state being threaded through props.
  useEffect(() => {
    document.documentElement.setAttribute(
      "data-sidebar",
      collapsed ? "rail" : "full"
    );
    try {
      window.localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch {
      /* storage unavailable */
    }
  }, [collapsed]);

  // The navbar's menu button opens the drawer on narrow screens.
  useEffect(() => {
    const open = () => setDrawerOpen((value) => !value);
    window.addEventListener(SIDEBAR_TOGGLE_EVENT, open);
    return () => window.removeEventListener(SIDEBAR_TOGGLE_EVENT, open);
  }, []);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  const handleLogout = useCallback(
    (event) => {
      event.preventDefault();
      authDispatch({ type: "LOGOUT" });
      navigate("/login");
    },
    [authDispatch, navigate]
  );

  useEffect(() => {
    if (!currentUser?.locationId) return;

    const locationRef = ref(database, `locations/${currentUser.locationId}`);
    get(locationRef).then((snapshot) => {
      if (!snapshot.exists()) {
        console.log("Location data not found");
        return;
      }
      if (snapshot.val().watchHealthAccess !== true) {
        authDispatch({ type: "LOGOUT" });
        alert(
          "This user's location is not currently subscribed to the Watch Health plan. Please contact support for more information."
        );
        navigate("/login");
      }
    });
  }, [currentUser, authDispatch, navigate]);

  const isHomeOrSelectWatch =
    location.pathname === "/" || location.pathname.includes("/selectWatch");

  const base = `/${currentUser?.companyId}/${currentUser?.locationId}`;

  const navItems = isHomeOrSelectWatch
    ? []
    : [
        {
          group: "Monitor",
          items: [
            {
              label: "Dashboard",
              to: `${base}/dashboard/${watchId || ""}`,
              icon: <DashboardOutlinedIcon />,
              match: "/dashboard",
            },
            {
              label: "Reports",
              to: `${base}/reports/${watchId || ""}`,
              icon: <DescriptionOutlinedIcon />,
              match: "/reports",
            },
            {
              label: "Files",
              to: `${base}/files/${watchId || ""}`,
              icon: <FolderOutlinedIcon />,
              match: "/files",
            },
          ],
        },
        {
          group: "System",
          items: [
            {
              label: "Body metrics scheduling",
              icon: <CalendarMonthOutlinedIcon />,
              onClick: () => setOpenScheduler(true),
            },
            {
              label: "Auto-delete settings",
              icon: <AutoDeleteOutlinedIcon />,
              onClick: () => setOpenAutoDeleteSettings(true),
            },
          ],
        },
      ];

  const renderLink = (item) => {
    const active = item.match && location.pathname.includes(item.match);
    const content = (
      <>
        {item.icon}
        {!collapsed && <span>{item.label}</span>}
      </>
    );

    const node = item.to ? (
      <Link
        key={item.label}
        to={item.to}
        className={`sidebar__link ${active ? "is-active" : ""}`}
        aria-current={active ? "page" : undefined}
      >
        {content}
      </Link>
    ) : (
      <button
        key={item.label}
        type="button"
        className={`sidebar__link ${item.danger ? "is-danger" : ""}`}
        onClick={item.onClick}
      >
        {content}
      </button>
    );

    return collapsed ? (
      <Tooltip key={item.label} title={item.label} placement="right">
        <span>{node}</span>
      </Tooltip>
    ) : (
      node
    );
  };

  return (
    <>
      {drawerOpen && (
        <button
          type="button"
          className="sidebar-scrim"
          aria-label="Close navigation"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <nav
        className={`sidebar ${collapsed ? "collapsed" : ""} ${
          drawerOpen ? "is-open" : ""
        }`}
        aria-label="Main navigation"
      >
        <div className="sidebar__brand">
          <span className="sidebar__mark">
            <MonitorHeartOutlinedIcon />
          </span>
          {!collapsed && (
            <span className="sidebar__wordmark">
              <strong>Watch Guardian</strong>
              <span>Health</span>
            </span>
          )}
          <Tooltip title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
            <IconButton
              size="small"
              className="sidebar__toggle"
              onClick={() => setCollapsed((value) => !value)}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? <MenuIcon /> : <MenuOpenIcon />}
            </IconButton>
          </Tooltip>
        </div>

        <div className="sidebar__nav">
          {navItems.map((section) => (
            <React.Fragment key={section.group}>
              <p className="sidebar__section">
                {collapsed ? section.group.slice(0, 3) : section.group}
              </p>
              {section.items.map(renderLink)}
            </React.Fragment>
          ))}
        </div>

        <div className="sidebar__footer">
          {renderLink({
            label: "Log out",
            icon: <LogoutOutlinedIcon />,
            onClick: handleLogout,
            danger: true,
          })}
        </div>
      </nav>

      <Dialog
        open={openScheduler}
        onClose={() => setOpenScheduler(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogContent dividers>
          <SchedulerComponent
            watchId={watchId}
            onClose={() => setOpenScheduler(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={openAutoDeleteSettings}
        onClose={() => setOpenAutoDeleteSettings(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogContent dividers>
          <AutoDeleteCommponent
            watchId={watchId}
            onClose={() => setOpenAutoDeleteSettings(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SideBarForRole7;
